/**
 * The Workflow Studio's Trigger stage — RO-3-02b.
 *
 * Port of three files: `_trigger.tsx` (435 LOC, the builder + the four-gate
 * activation step), `_trigger-ui.tsx` (228, the mode switch / run console / tip
 * strip / trace doorway) and `_run-loop-button.tsx` (130, the run control on the
 * manual console). They are one file here because they are one stage card with
 * one consumer, which is the reason `_trigger-ui.tsx`'s own header gives for not
 * promoting them into `packages/ui/src/blocks/` — "a block earns promotion when
 * a second consumer appears, not before."
 *
 * ── What changed, and only this ──
 *
 *   the four server actions   →  `apiCall(workflowDetail.*)`
 *   `next/link`               →  the router seam's `RouterLink`
 *   `useTransition`           →  a plain `pending` boolean; there is no server
 *                                render to transition INTO here, so the
 *                                transition was doing nothing a boolean doesn't
 *
 * `resolveTriggerActivation` is unchanged and still comes from core — through
 * the zero-dependency `@excellent/core/trigger-builder` subpath, never the
 * barrel (whose chain reaches the SQLCipher keystore). That function is the
 * FOUR-GATE derivation itself: pure, no imports, unit-tested. It is emphatically
 * not re-derived here.
 *
 * ── For the peer program (RECURSIVE-WORKFLOWS-COMPLETION-PRD) ──
 *
 * This is the surface an authored `onError` branch reaches the UI through: a
 * trigger is a MODE, and the stage's shape is "what starts a run" — the branch
 * itself is engine state, rendered from `WorkflowDetailPayload.trigger` and the
 * step list. Nothing here adds a predicate DSL or an edges table (its §7
 * non-goals); the engine's existing model is what is drawn.
 */

import * as React from "react";
import {
  Button,
  Card,
  Chip,
  FormField,
  Icon,
  Input,
  IntentBadge,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  Switch,
  cn,
  formatRelativeTime,
} from "@excellent/ui";
import {
  ArrowRight,
  Bot,
  Check,
  Copy,
  Lightbulb,
  MousePointerClick,
  Terminal,
} from "@excellent/ui/icons";
import { apiCall } from "@excellent/ui/api";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  resolveTriggerActivation,
  type EventTypeGroup,
  type TriggerActivation,
  type TriggerGateId,
} from "@excellent/core/trigger-builder";
import {
  workflowDetail,
  type WorkflowTriggerDto,
} from "@excellent/ui/api/contracts/workflows/detail";

const DEFAULT_DEBOUNCE = 0;
const DEFAULT_RATE = 60;

// ══ the mode switch ══════════════════════════════════════════════════════════

/**
 * Automatic on/off — the same pill-on-border switch shape the app uses for a
 * workflow's own on/off, so one shape means "this is live" everywhere.
 *
 * Named for the thing being switched ("Automatic off"), not framed as two equal
 * options: a Manual|Automatic segmented control hid which mode was in force. Off
 * is MUTED rather than destructive-red — manual is a perfectly good steady state,
 * and red would claim something is broken. Green means armed, and armed means
 * spending.
 */
function AutomaticSwitch({
  on,
  setOn,
  disabled,
}: {
  on: boolean;
  setOn: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        "flex h-7 shrink-0 items-center gap-2 rounded-full border border-border px-2.5 transition-colors",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
      )}
    >
      <Switch
        checked={on}
        onCheckedChange={setOn}
        disabled={disabled}
        size="sm"
        aria-label="Run this workflow automatically"
        className={cn(
          "data-[state=checked]:bg-success data-[state=unchecked]:bg-muted-foreground/40",
          "[&_[data-slot=switch-thumb]]:bg-white",
        )}
      />
      <span className="type-body font-medium text-foreground">Automatic {on ? "on" : "off"}</span>
    </label>
  );
}

/** The lightbulb footnote a section resolves to — bled to the card's edges. */
function TipStrip({ children }: { children: React.ReactNode }) {
  return (
    <div className="-mx-6 -mb-4 border-t border-border/60 bg-muted/30 px-6 py-3">
      <p className="flex items-start gap-2 type-subtext text-muted-foreground">
        <Lightbulb className="mt-px size-3.5 shrink-0 text-amber-400/80" />
        <span>{children}</span>
      </p>
    </div>
  );
}

// ══ the run console ══════════════════════════════════════════════════════════

interface RunSurface {
  key: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  detail: string;
  /** The exact string to copy. Null = this surface is a control, not a command. */
  code: string | null;
}

function runSurfaces(slug: string): RunSurface[] {
  return [
    {
      key: "app",
      icon: MousePointerClick,
      label: "From here",
      detail: "Run it now, with optional seed data for step 1.",
      code: null,
    },
    {
      key: "agent",
      icon: Bot,
      label: "From an agent",
      detail:
        "MCP tool. Call it with dry_run first to check it would start, without spending a token.",
      code: `loop_run { loop: "${slug}", dry_run: true }`,
    },
    {
      key: "cli",
      icon: Terminal,
      label: "From a terminal",
      detail: "The same detached runner the button spawns.",
      code: `excellent-mcp agent run-loop ${slug}`,
    },
  ];
}

/**
 * The three surfaces that start a run, on one console you flip through.
 *
 * Black, because this is a console: what's on it is a command you copy and paste
 * somewhere else. The dark plate is the page's one signal for "machine input,
 * verbatim" — which is what separates these rows from every other bordered row.
 */
function RunConsole({ slug, runControl }: { slug: string; runControl: React.ReactNode }) {
  const surfaces = React.useMemo(() => runSurfaces(slug), [slug]);
  const [active, setActive] = React.useState(surfaces[0]!.key);
  const cur = surfaces.find((s) => s.key === active) ?? surfaces[0]!;

  return (
    <div className="overflow-hidden rounded-md border border-white/10 bg-black">
      {/* Icon tabs. Icon AND label: three glyphs alone would be a guessing game
          about which one is the terminal, and the labels are two words each. */}
      <div className="flex flex-wrap items-center gap-1 border-b border-white/10 px-2 py-1.5">
        {surfaces.map((s) => {
          const on = s.key === cur.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setActive(s.key)}
              aria-pressed={on}
              className={cn(
                "flex items-center gap-1.5 rounded px-2 py-1 type-subtext font-medium transition-colors",
                on ? "bg-white/10 text-white" : "text-white/45 hover:bg-white/5 hover:text-white/80",
              )}
            >
              <s.icon className="size-3.5" />
              {s.label}
            </button>
          );
        })}
      </div>
      <div className="flex flex-col gap-3 px-4 py-3.5">
        <p className="type-subtext text-white/55">{cur.detail}</p>
        {cur.code ? <CommandLine code={cur.code} /> : runControl}
      </div>
    </div>
  );
}

/** A command on the console plate: prompt mark, the exact string, copy. */
function CommandLine({ code }: { code: string }) {
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1200);
    return () => window.clearTimeout(t);
  }, [copied]);

  return (
    <div className="flex items-center gap-2.5 rounded border border-white/10 bg-white/[0.04] px-2.5 py-2">
      <span className="select-none font-mono type-subtext text-white/30">$</span>
      <code className="min-w-0 flex-1 truncate font-mono type-subtext text-white/90">{code}</code>
      <button
        type="button"
        aria-label={copied ? "Copied" : "Copy command"}
        onClick={() => {
          void navigator.clipboard?.writeText(code);
          setCopied(true);
        }}
        className="shrink-0 rounded p-1 text-white/40 transition-colors hover:bg-white/10 hover:text-white"
      >
        {copied ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
      </button>
    </div>
  );
}

/**
 * Every trigger, manual or automatic, answers to the same trace.
 *
 * A plain styled `<Link>`, deliberately NOT a `<Button variant="ghost">`: the
 * dashed frame and auto height would mean re-painting the atom's height, border
 * and rounding, which the button grammar forbids. This is a doorway, not a
 * button.
 */
function TriggerTraceLink({ slug }: { slug: string }) {
  return (
    <Link
      href={`/workflows/${slug}/trace`}
      className="flex items-center justify-between gap-3 rounded-md border border-dashed border-border px-3 py-2.5 transition-colors hover:border-border/80 hover:bg-muted/40"
    >
      <span className="type-subtext text-muted-foreground">
        <span className="text-foreground">Trace</span> — every firing decision, including the events
        that did not run.
      </span>
      <span className="inline-flex shrink-0 items-center gap-1.5 type-subtext text-muted-foreground">
        View trace
        <ArrowRight className="size-3.5" />
      </span>
    </Link>
  );
}

// ══ the run control (port of `_run-loop-button.tsx`) ══════════════════════════

function parseEnv(text: string): { env: Record<string, string>; error: string | null } {
  const env: Record<string, string> = {};
  if (!text.trim()) return { env, error: null };
  for (const raw of text.split(/[\n,]+/)) {
    const line = raw.trim();
    if (!line) continue;
    const eq = line.indexOf("=");
    if (eq <= 0) return { env, error: `bad pair: "${line}" — expected KEY=value` };
    const key = line.slice(0, eq).trim();
    const value = line.slice(eq + 1).trim();
    if (!/^[A-Z][A-Z0-9_]*$/.test(key)) {
      return { env, error: `bad key "${key}" — must be SHOUTING_SNAKE` };
    }
    env[key] = value;
  }
  return { env, error: null };
}

/**
 * "Run workflow" — a workflow can take an arbitrary initial env (e.g.
 * `EXCELLENT_TARGET_URN=person:42` for an enrichment chain), so the typed
 * `KEY=value` pairs are parsed here and passed through. The endpoint spawns the
 * runner DETACHED, so this surfaces a transient "Started" and a link to where the
 * live runs appear.
 */
export function RunLoopButton({
  slug,
  missingRoles,
  initialEnvHint,
}: {
  slug: string;
  /** Roles that don't exist in the catalog — the workflow can't run at all. */
  missingRoles: string[];
  /** Display name(s) of env vars step 1 expects (placeholder hint). */
  initialEnvHint?: string;
}) {
  const [envText, setEnvText] = React.useState("");
  const [pending, setPending] = React.useState(false);
  const [feedback, setFeedback] = React.useState<
    { tone: "ok"; text: React.ReactNode } | { tone: "warn"; text: string } | null
  >(null);

  if (missingRoles.length > 0) {
    return (
      <span className="type-subtext text-muted-foreground">
        Blocked on{" "}
        <span className="font-mono text-foreground">
          {missingRoles.slice(0, 3).join(", ")}
          {missingRoles.length > 3 ? ` +${missingRoles.length - 3}` : ""}
        </span>{" "}
        ·{" "}
        <Link href="/" className="underline-offset-2 hover:underline">
          author role
        </Link>
      </span>
    );
  }

  function handleRun() {
    const parsed = parseEnv(envText);
    if (parsed.error) {
      setFeedback({ tone: "warn", text: parsed.error });
      return;
    }
    setFeedback(null);
    setPending(true);
    void (async () => {
      try {
        const res = await apiCall(workflowDetail.run, {
          params: { slug },
          body: { env: parsed.env },
        });
        if (res.ok) {
          setFeedback({
            tone: "ok",
            text: (
              <>
                Started.{" "}
                <Link href="/" className="underline-offset-2 hover:underline">
                  Watch on Pass
                </Link>
                .
              </>
            ),
          });
        } else {
          setFeedback({ tone: "warn", text: res.reason });
        }
      } catch (e) {
        setFeedback({ tone: "warn", text: e instanceof Error ? e.message : String(e) });
      } finally {
        setPending(false);
      }
    })();
  }

  return (
    <span className="flex items-center gap-2">
      <Input
        placeholder={initialEnvHint ?? "EXCELLENT_TARGET_URN=person:1"}
        value={envText}
        onChange={(e) => setEnvText(e.currentTarget.value)}
        // Width only. The atom owns height/padding/leading/text-size via its
        // `size` prop (input.tsx: sm h-6 · default h-7 · field h-8 · lg h-9),
        // so the original's `h-8 … type-subtext` repaint is not carried over — the
        // default h-7 is the same pairing this page's own sibling row uses
        // (studio-optimize.tsx:561, ported from _pipeline-actions.tsx:57):
        // a plain Input at default height beside a `size="sm"` action.
        className="w-[18rem]"
        spellCheck={false}
      />
      <Button size="sm" onClick={handleRun} disabled={pending}>
        {pending ? "Starting…" : "Run workflow"}
      </Button>
      {feedback ? (
        <span
          className={
            feedback.tone === "ok" ? "type-subtext text-muted-foreground" : "type-subtext text-destructive"
          }
          aria-live="polite"
        >
          {feedback.text}
        </span>
      ) : null}
    </span>
  );
}

// ══ the "When…" rule ═════════════════════════════════════════════════════════

/**
 * The event, the optional aggregate filter, and the two rails that bound it.
 *
 * Its own component because it owns its own state: six fields, one save, one
 * message. Left inline it put eleven `useState` calls and three handlers ahead of
 * the parent's first line of markup, which is the point at which a component has
 * stopped being one thing. The parent only needs to know a save happened and
 * whether it CREATED — hence the single `onSaved(created)`.
 */
function TriggerRuleForm({
  slug,
  eventGroups,
  subscription,
  hasSub,
  onSaved,
}: {
  slug: string;
  eventGroups: EventTypeGroup[];
  subscription: WorkflowTriggerDto["subscription"];
  hasSub: boolean;
  onSaved: (created: boolean) => void;
}) {
  const [eventType, setEventType] = React.useState(subscription?.event_type_prefix ?? "");
  const [aggregateKind, setAggregateKind] = React.useState(subscription?.aggregate_kind ?? "");
  const [name, setName] = React.useState(subscription?.name ?? "");
  const [debounce, setDebounce] = React.useState(
    String(subscription?.debounce_seconds ?? DEFAULT_DEBOUNCE),
  );
  const [rate, setRate] = React.useState(String(subscription?.max_fires_per_hour ?? DEFAULT_RATE));
  const [pending, setPending] = React.useState(false);
  const [msg, setMsg] = React.useState<{ tone: "ok" | "warn"; text: string } | null>(null);

  function handleSave() {
    if (!eventType) {
      setMsg({ tone: "warn", text: "Pick an event to trigger on." });
      return;
    }
    setPending(true);
    setMsg(null);
    void (async () => {
      try {
        const res = await apiCall(workflowDetail.saveTrigger, {
          params: { slug },
          body: {
            name,
            event_type_prefix: eventType,
            aggregate_kind: aggregateKind || null,
            debounce_seconds: Number(debounce) || 0,
            max_fires_per_hour: Number(rate) || DEFAULT_RATE,
          },
        });
        if (res.ok) {
          onSaved(res.created);
          setMsg({ tone: "ok", text: res.created ? "Trigger created." : "Trigger updated." });
        } else {
          setMsg({ tone: "warn", text: res.error });
        }
      } finally {
        setPending(false);
      }
    })();
  }

  return (
    <div className="flex flex-col gap-4 border-t border-border/40 pt-4">
      <p className="flex items-center gap-2 type-body font-medium text-foreground">
        <Icon name="zap" size="sm" className="text-muted-foreground" />
        When…
      </p>
      <p className="type-body text-muted-foreground">
        Pick the data change that should start a run. Only events the system actually emits are
        listed.
      </p>
      <div className="grid gap-4 @md/page:grid-cols-2">
        <FormField label="Trigger event" required htmlFor="trigger-event">
          <Select value={eventType} onValueChange={setEventType}>
            <SelectTrigger id="trigger-event">
              <SelectValue placeholder="Pick an event…" />
            </SelectTrigger>
            <SelectContent>
              {eventGroups.length === 0 ? (
                <SelectItem value="__none" disabled>
                  No emitted events yet
                </SelectItem>
              ) : (
                eventGroups.map((g) => (
                  <SelectGroup key={g.prefix}>
                    <SelectLabel>{g.label}</SelectLabel>
                    {g.options.map((o) => (
                      <SelectItem key={o.type} value={o.type}>
                        {o.label}
                        {o.description ? ` — ${o.description}` : ""}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))
              )}
            </SelectContent>
          </Select>
        </FormField>
        <FormField
          label="Only this aggregate"
          htmlFor="trigger-aggregate"
          help="Optional — restrict to one aggregate kind (e.g. a specific object type)."
        >
          <Input
            id="trigger-aggregate"
            value={aggregateKind}
            onChange={(e) => setAggregateKind(e.currentTarget.value)}
            placeholder="any"
          />
        </FormField>
      </div>
      <FormField label="Name" htmlFor="trigger-name" help="Shown in the review inbox when it fires.">
        <Input
          id="trigger-name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          placeholder={eventType ? `When ${eventType}…` : "When…"}
        />
      </FormField>
      <div className="grid gap-4 @md/page:grid-cols-2">
        <FormField
          label="Debounce (seconds)"
          htmlFor="trigger-debounce"
          help="Collapse a burst to one firing within this window."
        >
          <Input
            id="trigger-debounce"
            type="number"
            min={0}
            value={debounce}
            onChange={(e) => setDebounce(e.currentTarget.value)}
          />
        </FormField>
        <FormField
          label="Max fires / hour"
          htmlFor="trigger-rate"
          help="Hard cap so a bulk sync can't detonate token spend."
        >
          <Input
            id="trigger-rate"
            type="number"
            min={1}
            value={rate}
            onChange={(e) => setRate(e.currentTarget.value)}
          />
        </FormField>
      </div>
      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={pending || !eventType}>
          {pending ? "Saving…" : hasSub ? "Update trigger" : "Save trigger"}
        </Button>
        {msg && (
          <span
            className={
              msg.tone === "ok" ? "type-subtext text-muted-foreground" : "type-subtext text-destructive"
            }
            aria-live="polite"
          >
            {msg.text}
          </span>
        )}
      </div>
    </div>
  );
}

// ══ the stage card ═══════════════════════════════════════════════════════════

export function TriggerBuilder({
  slug,
  loopStatus: initialStatus,
  trigger,
  missingRoles,
  initialEnvHint,
}: {
  slug: string;
  loopStatus: string;
  trigger: WorkflowTriggerDto;
  missingRoles: string[];
  initialEnvHint: string;
}) {
  const { eventGroups, subscription, autorunEnabled: initialAutorun, drainerLive } = trigger;

  // Optimistic local mirrors of the four gate inputs so the activation step
  // recomputes the moment a toggle lands (before revalidation).
  const [loopStatus, setLoopStatus] = React.useState(initialStatus);
  const [subEnabled, setSubEnabled] = React.useState(subscription?.enabled ?? false);
  const [autorun, setAutorun] = React.useState(initialAutorun);
  const [hasSub, setHasSub] = React.useState(Boolean(subscription));

  // The mode. It opens on what is TRUE: automatic only if a trigger exists and is
  // enabled. A saved-but-disabled trigger opens manual — because that is what the
  // workflow actually does right now — with its rule still on file the moment the
  // switch goes back on.
  const [automatic, setAutomatic] = React.useState(Boolean(subscription?.enabled));

  const lastFiredAt = subscription?.last_fired_at ?? null;

  const [pending, setPending] = React.useState(false);
  const [msg, setMsg] = React.useState<{ tone: "ok" | "warn"; text: string } | null>(null);

  const activation: TriggerActivation = resolveTriggerActivation({
    hasSubscription: hasSub,
    loopStatus,
    subscriptionEnabled: subEnabled,
    autorunEnabled: autorun,
    drainerLive,
  });

  /**
   * The mode switch. Flipping it is not cosmetic — it is the trigger's own
   * `enabled` gate, so ON actually arms the saved rule and OFF actually disarms
   * it. With no rule saved yet there is nothing to enable, so ON just reveals the
   * form: you cannot arm a trigger that has no event.
   */
  function toggleAutomatic(next: boolean) {
    setAutomatic(next);
    if (!hasSub) return; // nothing to arm yet — the form is the next step
    setPending(true);
    setMsg(null);
    void (async () => {
      try {
        const r = await apiCall(workflowDetail.setTriggerEnabled, {
          params: { slug },
          body: { enabled: next },
        });
        if (r.ok) setSubEnabled(next);
        else setMsg({ tone: "warn", text: r.error ?? "Could not change the trigger." });
      } finally {
        setPending(false);
      }
    })();
  }

  // Only the three toggle gates reach here (the drainer gate is a link, never a
  // toggle) — but we accept the full `TriggerGateId` so the fix descriptor's token
  // slots straight in.
  function toggleGate(id: TriggerGateId) {
    setPending(true);
    setMsg(null);
    void (async () => {
      try {
        if (id === "workflow") {
          // `activateWorkflowAction` was `setLoopStatusAction(slug, "active")` —
          // so this calls that endpoint rather than a second one that only
          // curries an argument.
          const r = await apiCall(workflowDetail.setStatus, {
            params: { slug },
            body: { status: "active" },
          });
          if (r.ok) setLoopStatus("active");
          else setMsg({ tone: "warn", text: r.error ?? "Could not activate the workflow." });
        } else if (id === "subscription") {
          const next = !subEnabled;
          const r = await apiCall(workflowDetail.setTriggerEnabled, {
            params: { slug },
            body: { enabled: next },
          });
          if (r.ok) setSubEnabled(next);
          else setMsg({ tone: "warn", text: r.error ?? "Could not toggle the trigger." });
        } else if (id === "autorun") {
          const next = !autorun;
          const r = await apiCall(workflowDetail.setTriggerAutorun, {
            params: { slug },
            body: { enabled: next },
          });
          if (r.ok) setAutorun(next);
        }
      } finally {
        setPending(false);
      }
    })();
  }

  return (
    <Card className="flex flex-col gap-4 px-6 py-4">
      {/* ── The stage header — this Card IS the pipeline's Trigger stage card
          (the studio mounts it bare on the rail; a second framing Card around it
          double-titled the stage). Mode is a Chip beside the title, so the stat
          strip above and this card can never disagree about which mode is in
          force. ── */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
          <h3 className="type-title font-medium text-foreground">Trigger</h3>
          <Chip>{automatic ? "Automatic" : "Manual"}</Chip>
          {hasSub && lastFiredAt && (
            <span className="type-subtext text-muted-foreground">
              last fired {formatRelativeTime(lastFiredAt)}
            </span>
          )}
        </div>
        <AutomaticSwitch on={automatic} setOn={toggleAutomatic} disabled={pending} />
      </div>
      <p className="type-body text-muted-foreground">
        What <span className="text-foreground">starts</span> a run. The steps run on their own once a
        run begins — the trigger only decides when one begins.
      </p>

      {!automatic ? (
        <RunConsole
          slug={slug}
          runControl={
            <RunLoopButton
              slug={slug}
              missingRoles={missingRoles}
              initialEnvHint={initialEnvHint}
            />
          }
        />
      ) : (
        <>
          <TriggerRuleForm
            slug={slug}
            eventGroups={eventGroups}
            subscription={subscription}
            hasSub={hasSub}
            onSaved={(created) => {
              setHasSub(true);
              // A brand-new subscription lands enabled=0; a re-save preserves the
              // existing flag. Reflect the create case optimistically.
              if (created) setSubEnabled(true);
            }}
          />

          {/* ── Activation — the four gates ───────────────────────────────── */}
          <div className="flex flex-col border-t border-border/40 pt-4">
            <p className="flex items-center gap-2 type-body font-medium text-foreground">
              Activation
              {activation.armed ? (
                <IntentBadge intent="success" withDot>
                  Armed
                </IntentBadge>
              ) : (
                <IntentBadge intent="warning" withDot>
                  {activation.blocking.length} to go
                </IntentBadge>
              )}
            </p>
            <p className="mb-2 mt-1 type-body text-muted-foreground">
              {activation.armed
                ? "This trigger is armed — a matching event will run the workflow within one scheduler tick. It spends tokens without asking again; the rate cap is what bounds it."
                : "A trigger fires only when all four are green. Fix the ones below to arm it."}
            </p>
            {activation.gates.map((gate) => {
              const fix = gate.fix; // bind so TS narrows the union inside closures
              return (
                <div
                  key={gate.id}
                  className="flex items-start gap-3 border-b border-border/40 py-3 last:border-b-0"
                >
                  <Icon
                    name={gate.ok ? "circle-check" : "circle"}
                    size="sm"
                    className={gate.ok ? "mt-0.5 text-success/80" : "mt-0.5 text-muted-foreground"}
                  />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="type-body font-medium text-foreground">{gate.label}</span>
                    <span className="type-subtext text-muted-foreground">{gate.detail}</span>
                  </div>
                  <div className="shrink-0">
                    {fix?.kind === "toggle" ? (
                      <Switch
                        checked={gate.ok}
                        disabled={pending}
                        onCheckedChange={() => toggleGate(fix.token)}
                        aria-label={`Toggle ${gate.label}`}
                      />
                    ) : fix?.kind === "link" ? (
                      <Button asChild variant="outline" size="sm">
                        <Link href={fix.href}>Open settings</Link>
                      </Button>
                    ) : gate.ok ? (
                      <IntentBadge intent="success">On</IntentBadge>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {msg && (
        <p
          className={
            msg.tone === "ok" ? "type-subtext text-muted-foreground" : "type-subtext text-destructive"
          }
          aria-live="polite"
        >
          {msg.text}
        </p>
      )}

      <TriggerTraceLink slug={slug} />

      {/* The footnote the whole section resolves to. */}
      <TipStrip>
        {automatic
          ? "A matching event starts a run on its own — but only while all four gates are green. Every firing is recorded, including the ones held back, so an automatic trigger stays accountable."
          : "Nothing starts this workflow on its own. A person or an agent runs it — through any of three surfaces, all landing in the same runner and the same audit trail."}
      </TipStrip>
    </Card>
  );
}
